const PITCHER_POSITIONS = new Set(['SP', 'RP', 'P']);

/** Normalize position_eligibility from DB — may be string[], Record<string,number>, or null */
export function toArray(positionEligibility: string[] | Record<string, number> | null | undefined): string[] | null {
  if (!positionEligibility) return null;
  if (Array.isArray(positionEligibility)) return positionEligibility;
  if (typeof positionEligibility === 'object') return Object.keys(positionEligibility);
  return null;
}

const SLOT_POSITION_MAP: Record<string, Set<string>> = {
  'C': new Set(['C']),
  '1B': new Set(['1B']),
  '2B': new Set(['2B']),
  '3B': new Set(['3B']),
  'SS': new Set(['SS']),
  'LF': new Set(['LF']),
  'CF': new Set(['CF']),
  'RF': new Set(['RF']),
  'OF': new Set(['LF', 'CF', 'RF', 'OF']),
  'SP': new Set(['SP', 'P']),
  'RP': new Set(['RP', 'P']),
  'P': new Set(['SP', 'RP', 'P']),
  'DH': new Set(['DH']),
  'IL': new Set(), // any player
};

/**
 * Check if a player can play a given roster slot.
 * UTIL is handled internally — any non-pitcher can play UTIL without it being stored in position_eligibility.
 */
export function canPlaySlot(
  primaryPosition: string,
  slot: string,
  positionEligibility?: string[] | Record<string, number> | null,
): boolean {
  if (slot === 'IL') return true;
  const elig = toArray(positionEligibility);

  // Strip trailing digits from slot (e.g. 'SP1' -> 'SP', 'OF2' -> 'OF')
  const baseSlot = slot.replace(/\d+$/, '');

  // UTIL: any non-pitcher can fill it
  if (baseSlot === 'UTIL') {
    const allPositions = [primaryPosition, ...(elig || [])];
    return allPositions.some(p => !PITCHER_POSITIONS.has(p));
  }

  const allowed = SLOT_POSITION_MAP[baseSlot];
  if (!allowed) return false;

  // Check primary position
  if (allowed.has(primaryPosition)) return true;

  // Check eligible positions
  if (elig) {
    for (const pos of elig) {
      if (allowed.has(pos)) return true;
    }
  }

  return false;
}

/**
 * Format position eligibility as a slash-separated string.
 * If OF is present, drops LF/CF/RF since OF implies all three.
 * e.g. ['LF','OF','CF','RF'] -> 'OF', ['2B','3B','LF','OF'] -> '2B/3B/OF'
 * Returns empty string if no eligible positions.
 */
export function formatEligibility(positionEligibility?: string[] | Record<string, number> | null): string {
  const elig = toArray(positionEligibility);
  if (!elig || elig.length === 0) return '';
  let positions = elig;
  if (positions.includes('OF')) {
    positions = positions.filter(p => p !== 'LF' && p !== 'CF' && p !== 'RF');
  }
  return positions.join('/');
}

/**
 * Format a player's full position display: primary position + eligible positions,
 * deduped and collapsed (OF absorbs LF/CF/RF).
 * e.g. primaryPosition='SS', eligibility=['SS','2B','3B'] -> 'SS/2B/3B'
 * e.g. primaryPosition='LF', eligibility=['LF','CF','RF','OF'] -> 'OF'
 * Falls back to just primaryPosition if no eligibility data.
 */
export function formatPositionDisplay(primaryPosition: string, positionEligibility?: string[] | Record<string, number> | null): string {
  const elig = toArray(positionEligibility);
  if (!elig || elig.length === 0) return primaryPosition;
  // Merge primary + eligibility, dedup, preserve primary-first order
  const all = [primaryPosition, ...elig.filter(p => p !== primaryPosition)];
  // Collapse OF
  if (all.includes('OF')) {
    const filtered = all.filter(p => p !== 'LF' && p !== 'CF' && p !== 'RF');
    return filtered.join('/');
  }
  return all.join('/');
}
